"use client";

import { Suspense, useState, useEffect, useRef } from "react";
import { getProviders, signIn } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import { LoginPageSkeleton } from "@/components/loading/page-skeletons";

// Suspense próprio pro useSearchParams (regra do Next) — como esta página é
// estática (sem force-dynamic), este fallback é o que aparece de fato até a
// hidratação resolver os search params; por isso usa o MESMO esqueleto do
// loading.tsx da rota (src/app/login/loading.tsx), não um texto solto.
export default function LoginPage() {
  return (
    <Suspense fallback={<LoginPageSkeleton />}>
      <LoginForm />
    </Suspense>
  );
}

function LoginForm() {
  const router = useRouter();
  const params = useSearchParams();
  const callbackUrl = params.get("callbackUrl") ?? "/";
  const initialMode = params.get("mode");
  const initialError = params.get("erro");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [mode, setMode] = useState<"password" | "otp" | "whatsapp">(
    initialMode === "otp" ? "otp" : initialMode === "whatsapp" ? "whatsapp" : "password",
  );
  const [stage, setStage] = useState<"email" | "code">("email");
  const [telefone, setTelefone] = useState("");
  const [nascimento, setNascimento] = useState("");
  const [pedirNascimento, setPedirNascimento] = useState(false);
  const [destino, setDestino] = useState<{ canal: string; mascara: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(
    initialError === "google-indisponivel"
      ? "Login com Google ainda não foi configurado neste ambiente. Use email e senha ou código."
      : null,
  );

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const res = await signIn("password", {
      email,
      password,
      redirect: false,
    });
    setLoading(false);
    if (res?.error) {
      setError("email ou senha inválidos");
      return;
    }
    router.push(callbackUrl);
    router.refresh();
  }

  async function submitGoogle() {
    setError(null);
    setLoading(true);
    await signIn("google", { callbackUrl });
  }

  // Apple só aparece quando o provider está configurado no servidor (mesmo
  // gate por env do brito-auth); o Google mantém o comportamento histórico.
  const [appleDisponivel, setAppleDisponivel] = useState(false);
  useEffect(() => {
    getProviders().then((p) => setAppleDisponivel(Boolean(p?.apple)));
  }, []);

  async function submitApple() {
    setError(null);
    setLoading(true);
    await signIn("apple", { callbackUrl });
  }

  // SSO com mode=google (vindo de /api/sso/start) auto-dispara o Google pelo
  // caminho correto do next-auth/react. Antes o /api/sso/start fazia um GET
  // direto p/ /api/auth/signin/google, que o Auth.js v5 recusa (UnknownAction).
  const googleAutoStarted = useRef(false);
  useEffect(() => {
    if (initialMode === "google" && !googleAutoStarted.current) {
      googleAutoStarted.current = true;
      void submitGoogle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMode]);

  async function requestCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/otp", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error ?? "falha ao enviar código");
      }
      setStage("code");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const res = await signIn("credentials", {
      email,
      code,
      redirect: false,
    });
    setLoading(false);
    if (res?.error) {
      setError("código inválido ou expirado");
      return;
    }
    router.push(callbackUrl);
    router.refresh();
  }

  async function requestPhoneCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/otp-telefone", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ telefone, dataNascimento: nascimento || undefined }),
      });
      const j = await res.json().catch(() => ({}));
      if (j.precisaNascimento) {
        setPedirNascimento(true);
        setError("Esse telefone é compartilhado — informe sua data de nascimento.");
        return;
      }
      if (!res.ok) throw new Error(j.error ?? "falha ao enviar código");
      setDestino({ canal: j.canal, mascara: j.destino });
      setStage("code");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  async function submitPhoneCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const res = await signIn("phone-otp", {
      telefone,
      dataNascimento: nascimento || undefined,
      code,
      redirect: false,
    });
    setLoading(false);
    if (res?.error) {
      setError("código inválido ou expirado");
      return;
    }
    router.push(callbackUrl);
    router.refresh();
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-100 p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="text-5xl mb-3">🔐</div>
          <h1 className="text-2xl font-semibold text-orange-400">Maranata Key</h1>
          <p className="text-sm text-slate-400 mt-1">Identidade e acesso da IME Maranata</p>
        </div>

        <div className="bg-slate-900 rounded-2xl p-6 shadow-xl">
          <button
            type="button"
            onClick={submitGoogle}
            disabled={loading} aria-busy={loading}
            className="mb-4 flex w-full items-center justify-center gap-2 rounded-lg border border-slate-700 bg-white px-4 py-2 font-medium text-slate-950 hover:bg-slate-100 disabled:opacity-50"
          >
            Entrar com Google
          </button>

          {appleDisponivel && (
            <button
              type="button"
              onClick={submitApple}
              disabled={loading} aria-busy={loading}
              className="mb-4 flex w-full items-center justify-center gap-2 rounded-lg border border-slate-700 bg-black px-4 py-2 font-medium text-white hover:bg-slate-900 disabled:opacity-50"
            >
              <svg width="15" height="15" viewBox="0 0 814 1000" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"
                />
              </svg>
              Entrar com Apple
            </button>
          )}

          <div className="mb-5 flex items-center gap-3 text-xs uppercase tracking-wider text-slate-500">
            <span className="h-px flex-1 bg-slate-800" />
            <span>ou</span>
            <span className="h-px flex-1 bg-slate-800" />
          </div>

          <div className="mb-5 grid grid-cols-3 rounded-lg bg-slate-800 p-1 text-sm">
            <button
              type="button"
              onClick={() => { setMode("password"); setError(null); }}
              className={`rounded-md px-2 py-2 ${mode === "password" ? "bg-orange-500 text-slate-950 font-medium" : "text-slate-400"}`}
            >
              Senha
            </button>
            <button
              type="button"
              onClick={() => { setMode("otp"); setStage("email"); setError(null); }}
              className={`rounded-md px-2 py-2 ${mode === "otp" ? "bg-orange-500 text-slate-950 font-medium" : "text-slate-400"}`}
            >
              E-mail
            </button>
            <button
              type="button"
              onClick={() => { setMode("whatsapp"); setStage("email"); setError(null); }}
              className={`rounded-md px-2 py-2 ${mode === "whatsapp" ? "bg-orange-500 text-slate-950 font-medium" : "text-slate-400"}`}
            >
              WhatsApp
            </button>
          </div>

          {mode === "password" && (
            <form onSubmit={submitPassword} className="space-y-4">
              <label className="block">
                <span className="text-sm text-slate-300">Email</span>
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="seu@email.com"
                  autoComplete="username"
                />
              </label>
              <label className="block">
                <span className="text-sm text-slate-300">Senha</span>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  autoComplete="current-password"
                />
              </label>
              <button
                type="submit"
                disabled={loading} aria-busy={loading}
                className="w-full rounded-lg bg-orange-500 hover:bg-orange-400 disabled:opacity-50 px-4 py-2 font-medium text-slate-950"
              >
                {loading ? "Entrando..." : "Entrar"}
              </button>
            </form>
          )}

          {mode === "otp" && stage === "email" && (
            <form onSubmit={requestCode} className="space-y-4">
              <label className="block">
                <span className="text-sm text-slate-300">Email</span>
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="seu@email.com"
                />
              </label>
              <button
                type="submit"
                disabled={loading} aria-busy={loading}
                className="w-full rounded-lg bg-orange-500 hover:bg-orange-400 disabled:opacity-50 px-4 py-2 font-medium text-slate-950"
              >
                {loading ? "Enviando..." : "Enviar código"}
              </button>
            </form>
          )}

          {mode === "otp" && stage === "code" && (
            <form onSubmit={submitCode} className="space-y-4">
              <p className="text-sm text-slate-400">
                Código enviado para <span className="text-slate-200">{email}</span>
              </p>
              <label className="block">
                <span className="text-sm text-slate-300">Código (6 dígitos)</span>
                <input
                  type="text"
                  required
                  autoFocus
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="mt-1 w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-2xl tracking-widest text-center text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="000000"
                />
              </label>
              <button
                type="submit"
                disabled={loading} aria-busy={loading}
                className="w-full rounded-lg bg-orange-500 hover:bg-orange-400 disabled:opacity-50 px-4 py-2 font-medium text-slate-950"
              >
                {loading ? "Validando..." : "Entrar"}
              </button>
              <button
                type="button"
                onClick={() => { setStage("email"); setCode(""); setError(null); }}
                className="w-full text-sm text-slate-400 hover:text-slate-200"
              >
                ← usar outro email
              </button>
            </form>
          )}

          {mode === "whatsapp" && stage === "email" && (
            <form onSubmit={requestPhoneCode} className="space-y-4">
              <label className="block">
                <span className="text-sm text-slate-300">Telefone com DDD</span>
                <input
                  type="tel"
                  required
                  autoFocus
                  value={telefone}
                  onChange={(e) => setTelefone(e.target.value)}
                  className="mt-1 w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="(21) 99999-9999"
                  autoComplete="tel"
                />
              </label>
              {pedirNascimento && (
                <label className="block">
                  <span className="text-sm text-slate-300">Data de nascimento</span>
                  <input
                    type="date"
                    required
                    value={nascimento}
                    onChange={(e) => setNascimento(e.target.value)}
                    className="mt-1 w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </label>
              )}
              <button
                type="submit"
                disabled={loading} aria-busy={loading}
                className="w-full rounded-lg bg-orange-500 hover:bg-orange-400 disabled:opacity-50 px-4 py-2 font-medium text-slate-950"
              >
                {loading ? "Enviando..." : "Receber código no WhatsApp"}
              </button>
              <p className="text-xs text-slate-500 text-center">
                O código vai pro telefone do seu cadastro. Sem WhatsApp no
                momento, ele chega no seu e-mail cadastrado.
              </p>
            </form>
          )}

          {mode === "whatsapp" && stage === "code" && (
            <form onSubmit={submitPhoneCode} className="space-y-4">
              <p className="text-sm text-slate-400">
                Código enviado por{" "}
                <span className="text-slate-200">
                  {destino?.canal === "email" ? "e-mail" : destino?.canal ?? "WhatsApp"}
                </span>{" "}
                para <span className="text-slate-200">{destino?.mascara}</span>
              </p>
              <label className="block">
                <span className="text-sm text-slate-300">Código (6 dígitos)</span>
                <input
                  type="text"
                  required
                  autoFocus
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="mt-1 w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-2xl tracking-widest text-center text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="000000"
                />
              </label>
              <button
                type="submit"
                disabled={loading} aria-busy={loading}
                className="w-full rounded-lg bg-orange-500 hover:bg-orange-400 disabled:opacity-50 px-4 py-2 font-medium text-slate-950"
              >
                {loading ? "Validando..." : "Entrar"}
              </button>
              <button
                type="button"
                onClick={() => { setStage("email"); setCode(""); setError(null); }}
                className="w-full text-sm text-slate-400 hover:text-slate-200"
              >
                ← usar outro telefone
              </button>
            </form>
          )}

          {error && (
            <p className="mt-4 text-sm text-rose-400 text-center">{error}</p>
          )}
        </div>
      </div>
    </div>
  );
}
